import { useState, useCallback, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { generateTxId } from '../utils/transactionSync';

const AUTO_SCAN_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 hours
const AUTO_SCAN_STORAGE_KEY = 'investportpro_last_dividend_scan';

/**
 * แปลงวันที่ทุกรูปแบบให้เป็นสตริง YYYY-MM-DD เพื่อเปรียบเทียบ
 * โดยไม่ผ่าน Date Object → ป้องกัน Timezone offset ที่ทำให้วันที่คลาดเคลื่อน
 * เช่น "2024-06-10T00:00:00" (no TZ) ใน GMT+7 จะถูก JS แปลงเป็น
 * 2024-06-09T17:00:00Z ซึ่งผิดพลาดเมื่อเทียบกับ "2024-06-10" (UTC 00:00)
 */
const normalizeDateStr = (dateVal) => {
  if (!dateVal) return '';
  // ถ้าเป็น Date Object → แปลงเป็น ISO string แล้วตัด
  if (dateVal instanceof Date) return dateVal.toISOString().slice(0, 10);
  // ถ้าเป็น string → ตัดเอาแค่ 10 ตัวแรก (YYYY-MM-DD) โดยไม่แปลง TZ
  return String(dateVal).slice(0, 10);
};

/**
 * Calculate the number of shares held for a given symbol on a specific date
 * by replaying BUY/SELL transactions up to (but NOT including) the XD date.
 * ★ ใช้ normalizeDateStr เปรียบเทียบ string ตรงๆ ไม่ผ่าน new Date()
 *   เพื่อป้องกันปัญหา Timezone ใน GMT+7 และ TZ อื่นๆ
 */
const getSharesHeldOnDate = (transactions, symbol, xdDate) => {
  const xdStr = normalizeDateStr(xdDate); // เช่น "2024-06-10"
  const upperSymbol = symbol.toUpperCase();
  let shares = 0;

  // เรียงลำดับตามวันที่จากเก่าสุดไปใหม่สุด โดยเปรียบเทียบ string YYYY-MM-DD
  const sorted = [...transactions]
    .filter(t => (t.symbol || '').toUpperCase() === upperSymbol && (t.type === 'BUY' || t.type === 'SELL'))
    .sort((a, b) => {
      const da = normalizeDateStr(a.date);
      const db = normalizeDateStr(b.date);
      return da < db ? -1 : da > db ? 1 : 0;
    });

  for (const t of sorted) {
    const txDateStr = normalizeDateStr(t.date);
    // หยุดทันทีเมื่อถึงธุรกรรมที่เกิดขึ้น ณ วัน XD หรือหลังจากนั้น
    if (txDateStr >= xdStr) break;
    if (t.type === 'BUY') shares += parseFloat(t.quantity);
    else if (t.type === 'SELL') shares -= parseFloat(t.quantity);
  }

  return Math.max(0, shares);
};

/**
 * Check if a dividend record already exists in existing transactions.
 * ★ Precise match by ex-date: ex-date uniquely identifies each payout, so monthly
 *   ETFs (QQQI/SPYI) whose consecutive months are ~28 days apart with near-identical
 *   DPS are no longer wrongly merged into one.
 */
const isDividendAlreadyRecorded = (transactions, symbol, xdDate, dps) => {
  const xd = new Date(xdDate);
  const upperSymbol = symbol.toUpperCase();

  return transactions.some(t => {
    if (t.type !== 'DIVIDEND' || (t.symbol || '').toUpperCase() !== upperSymbol) return false;
    // 1. Exact ex-date match (newer auto-detected records store exDate)
    if (t.exDate && xdDate) return t.exDate === xdDate;
    // 2. Legacy/manual records without exDate: tight ±10-day window
    //    (clearly separates monthly payouts ≥28 days apart, while still catching
    //     the same dividend's ex↔payment-date gap)
    const txDate = new Date(t.date);
    const daysDiff = Math.abs((txDate - xd) / (1000 * 60 * 60 * 24));
    const dpsDiff = Math.abs(parseFloat(t.price) - dps);
    return daysDiff <= 10 && dpsDiff < 0.05;
  });
};

/**
 * Core scan logic — uses pre-fetched dividend data from Firestore (NO API calls!)
 * Reads from global_state/dividend_calendar which is updated by backend at 6 AM daily.
 */
const runDividendScan = async (transactions, dividendCalendar, onProgress) => {
  if (!transactions || transactions.length === 0) return [];
  if (!dividendCalendar || dividendCalendar.length === 0) return [];

  const cryptoKeywords = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE'];
  const uniqueSymbols = [...new Set(
    transactions
      .filter(t => t.type === 'BUY')
      .filter(t => !cryptoKeywords.some(c => t.symbol.toUpperCase().includes(c)))
      .filter(t => t.assetClass !== 'Crypto')
      .map(t => t.symbol.toUpperCase())
  )].sort();

  const allDetected = [];

  for (let i = 0; i < uniqueSymbols.length; i++) {
    const symbol = uniqueSymbols[i];
    if (onProgress) onProgress({ current: i + 1, total: uniqueSymbols.length, symbol });

    // Filter dividends from the calendar for this symbol
    const symbolDividends = dividendCalendar.filter(
      d => (d.symbol || '').toUpperCase() === symbol
    );

    if (symbolDividends.length === 0) continue;

    for (const div of symbolDividends) {
      const xdDate = div.exDate || div.date;
      if (!xdDate) continue;
      const dps = parseFloat(div.amount);
      if (!dps || dps <= 0) continue;

      // ★ Record the dividend on the ACTUAL payment date (when money arrives),
      //   never on the XD date.
      const recordDateStr = div.paymentDate || xdDate;
      const payDateObj = div.paymentDate ? new Date(div.paymentDate) : null;
      const exDateObj = new Date(xdDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysSinceEx = Math.floor((today - exDateObj) / (1000 * 60 * 60 * 24));
      if (payDateObj) {
        // Real payment date is known → only record once it has actually arrived.
        if (payDateObj > today) continue;
      } else if (daysSinceEx < 14) {
        // Payment date not published yet → wait up to 14 days for the provider to fill
        // it in, so the record lands on the real pay date instead of the XD date.
        continue;
      }

      const sharesHeld = getSharesHeldOnDate(transactions, symbol, xdDate);
      if (sharesHeld <= 0) continue;

      if (isDividendAlreadyRecorded(transactions, symbol, xdDate, dps)) continue;

      const grossAmount = sharesHeld * dps;
      const tax = grossAmount * 0.15;
      const netAmount = grossAmount - tax;

      allDetected.push({
        symbol,
        xdDate,
        recordDate: recordDateStr, // The actual date the transaction will use
        dps: parseFloat(dps.toFixed(4)),
        sharesHeld,
        grossAmount: parseFloat(grossAmount.toFixed(2)),
        tax: parseFloat(tax.toFixed(4)),
        netAmount: parseFloat(netAmount.toFixed(2)),
      });
    }
  }

  allDetected.sort((a, b) => new Date(b.recordDate) - new Date(a.recordDate));
  return allDetected;
};

/**
 * Custom hook for auto-detecting dividends.
 * 
 * ★ v2: NO MORE API CALLS! 
 * Reads from global_state/dividend_calendar (populated by backend at 6 AM daily).
 * Auto-detection is also done server-side now, so the frontend only handles
 * notification UI and manual scan for review.
 */
export const useAutoDividends = (transactions, setTransactions) => {
  const [detectedDividends, setDetectedDividends] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0, symbol: '' });
  const [error, setError] = useState(null);
  const [autoAddedCount, setAutoAddedCount] = useState(0);
  const [autoAddedDetails, setAutoAddedDetails] = useState([]);
  const autoScanRanRef = useRef(false);
  const [dividendCalendar, setDividendCalendar] = useState(null);

  // 1. Subscribe to dividend calendar from Firestore (free, real-time)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'global_state', 'dividend_calendar'),
      (snap) => {
        if (snap.exists()) {
          setDividendCalendar(snap.data().dividends || []);
        }
      },
      (err) => {
        console.warn('useAutoDividends: dividend_calendar snapshot error', err);
      }
    );
    return () => unsubscribe();
  }, []);

  // Manual scan — shows results for user to review (NO API calls!)
  const scanForDividends = useCallback(async () => {
    if (!transactions || transactions.length === 0) return;
    if (!dividendCalendar) {
      setError('Dividend data not loaded yet. Please try again.');
      return;
    }

    setIsScanning(true);
    setError(null);
    setDetectedDividends([]);

    try {
      const results = await runDividendScan(transactions, dividendCalendar, (progress) => {
        setScanProgress(progress);
      });
      setDetectedDividends(results);
    } catch (err) {
      setError(err.message);
      console.error('Manual dividend scan error:', err);
    } finally {
      setIsScanning(false);
    }
  }, [transactions, dividendCalendar]);

  // Auto scan on app load — runs locally using Firestore data (NO API calls!)
  // ★ Note: The backend also does this at 6 AM daily, so this is a backup
  //   that catches any dividends the backend may have added since last app load.
  //   It reads from Firestore → zero API credits.
  useEffect(() => {
    if (autoScanRanRef.current) return;
    if (!transactions || transactions.length === 0) return;
    if (!setTransactions) return;
    if (!dividendCalendar || dividendCalendar.length === 0) return;

    // Check cooldown
    const lastScan = localStorage.getItem(AUTO_SCAN_STORAGE_KEY);
    if (lastScan) {
      const elapsed = Date.now() - parseInt(lastScan, 10);
      if (elapsed < AUTO_SCAN_COOLDOWN_MS) {
        console.log(`[AutoDividend] Skipping — last scan was ${Math.round(elapsed / 60000)} min ago`);
        return;
      }
    }

    autoScanRanRef.current = true;

    // Delay auto-scan by 5 seconds so the app finishes loading first
    const timer = setTimeout(async () => {
      console.log('[AutoDividend] Starting background scan (Firestore-only, 0 API credits)...');
      setIsScanning(true);

      try {
        const results = await runDividendScan(transactions, dividendCalendar, (progress) => {
          setScanProgress(progress);
        });

        if (results.length > 0) {
          // Auto-add all detected dividends to portfolio
          const newTxs = results.map(d => ({
            id: generateTxId(),
            date: d.recordDate, // Use payment date for the transaction
            exDate: d.xdDate, // ★ Store ex-date for precise future de-duplication
            type: 'DIVIDEND',
            assetClass: 'US Stock',
            symbol: d.symbol,
            price: d.dps,
            quantity: d.sharesHeld,
            feeTax: d.tax.toFixed(4),
            account: '',
            autoDetected: true, // Flag to identify auto-added records
            createdAt: Date.now(),
            updatedAt: new Date().toISOString(),
          }));

          setTransactions(prev => [...prev, ...newTxs]);
          setAutoAddedCount(results.length);
          setAutoAddedDetails(results);
          console.log(`[AutoDividend] Auto-added ${results.length} dividends to portfolio`);
        } else {
          console.log('[AutoDividend] No new dividends found');
        }

        // Save scan timestamp
        localStorage.setItem(AUTO_SCAN_STORAGE_KEY, Date.now().toString());

      } catch (err) {
        console.error('[AutoDividend] Background scan error:', err);
      } finally {
        setIsScanning(false);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [transactions, setTransactions, dividendCalendar]); // eslint-disable-line react-hooks/exhaustive-deps

  return { 
    detectedDividends, setDetectedDividends, 
    isScanning, scanProgress, error, 
    scanForDividends,
    autoAddedCount, setAutoAddedCount,
    autoAddedDetails
  };
};
